import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BarChart3, Calendar, Download, Filter, RefreshCw, Trophy, Truck, User, X } from 'lucide-react';
import Card, { CardContent, CardHeader } from '../ui/Card';
import Button from '../ui/Button';
import { supabase } from '../../lib/supabase';
import { objectsToCsv, downloadCsv } from '../../utils/csvUtils';
import { formatCurrency } from '../../utils/formatters';
import { formatArgentinaDate } from '../../utils/dateUtils';
import { useAppStore } from '../../store';

interface Stop {
  id: string;
  address: string;
  lat: number;
  lng: number;
  name?: string;
  notes?: string;
}

interface Trip {
  id: string;
  name: string;
  driver_id: string;
  date: string;
  cost_per_km: number;
  total_distance: number;
  total_cost: number;
  stops: Stop[];
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
}

interface Driver {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  vehicle_type?: string | null;
  vehicle_plate?: string | null;
  notes?: string | null;
}

const STATUS_LABELS: Record<Trip['status'], string> = {
  completed: 'Completados',
  in_progress: 'En progreso',
  planned: 'Planificados',
  cancelled: 'Cancelados',
};

interface LogisticsAnalyticsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LogisticsAnalyticsModal: React.FC<LogisticsAnalyticsModalProps> = ({ isOpen, onClose }) => {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [dateStart, setDateStart] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10));
  const [dateEnd, setDateEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [driverFilter, setDriverFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [includeTripDetail, setIncludeTripDetail] = useState(false);
  const { setError } = useAppStore();

  const datePresets = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const end = today;
    const mk = (daysBack: number) => {
      const d = new Date();
      d.setDate(d.getDate() - daysBack);
      return d.toISOString().slice(0, 10);
    };
    return [
      { id: '7d', label: '7 días', start: mk(6), end },
      { id: '30d', label: '30 días', start: mk(29), end },
      { id: '90d', label: '90 días', start: mk(89), end },
      { id: 'mtd', label: 'Mes actual', start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10), end },
      { id: 'ytd', label: 'Año actual', start: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10), end },
      { id: 'today', label: 'Hoy', start: today, end: today },
    ] as const;
  }, []);

  const loadDrivers = async () => {
    try {
      const { data, error } = await supabase
        .from('logistics_drivers')
        .select('id, name, phone, email, vehicle_type, vehicle_plate, notes')
        .order('name', { ascending: true });
      if (error) throw error;
      setDrivers((data || []).filter((d: any) => d.name));
    } catch (e: any) {
      setDrivers([]);
    }
  };

  const loadTrips = async () => {
    if (!isOpen) return;
    setIsLoading(true);
    try {
      let q = supabase
        .from('logistics_trips')
        .select('*')
        .gte('date', dateStart)
        .lte('date', dateEnd)
        .order('date', { ascending: false });

      if (driverFilter !== 'all') q = q.eq('driver_id', driverFilter);
      if (statusFilter !== 'all') q = q.eq('status', statusFilter);

      const { data, error } = await q;

      if (error) throw error;

      const parsed: Trip[] = (data || []).map((trip: any) => ({
        ...trip,
        stops: typeof trip.stops === 'string' ? (() => {
          try { return JSON.parse(trip.stops); } catch { return []; }
        })() : (trip.stops || []),
      }));
      setTrips(parsed);
    } catch (error: any) {
      setError(error.message || 'Error al cargar analíticas');
      setTrips([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) loadDrivers();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) void loadTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, dateStart, dateEnd, driverFilter, statusFilter]);

  const driverById = useMemo(() => {
    const m = new Map<string, Driver>();
    for (const d of drivers) m.set(d.id, d);
    return m;
  }, [drivers]);

  const driverNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of drivers) m.set(d.id, d.name);
    return m;
  }, [drivers]);

  const SUMMARY_HEADERS = [
    'Conductor', 'Viajes', 'Costo total', 'Costo / viaje', 'Km totales',
    'Costo / km', 'Paradas totales', 'Paradas / viaje',
  ] as const;

  const DETAIL_HEADERS = [
    'Viaje ID',
    'Nombre viaje',
    'Fecha viaje',
    'Estado viaje',
    'Transportista',
    'Teléfono transportista',
    'Email transportista',
    'Tipo vehículo',
    'Patente',
    'Notas transportista',
    'Costo por km',
    'Distancia total km',
    'Costo total viaje',
    'Total paradas viaje',
    'Parada nro',
    'Nombre parada',
    'Dirección parada',
    'Latitud parada',
    'Longitud parada',
    'Notas parada',
    'Creado',
    'Actualizado',
  ] as const;

  const buildSummaryRows = () => driverRankingByTotalCost.map(s => ({
    Conductor: s.driverName,
    Viajes: s.trips,
    'Costo total': s.totalCost,
    'Costo / viaje': s.costPerTrip,
    'Km totales': s.totalKm,
    'Costo / km': s.costPerKm,
    'Paradas totales': s.totalStops,
    'Paradas / viaje': s.stopsPerTrip,
  }));

  const buildTripDetailRows = () => {
    const rows: Record<string, string | number>[] = [];

    for (const trip of [...trips].sort((a, b) => b.date.localeCompare(a.date) || a.name.localeCompare(b.name))) {
      const driver = trip.driver_id ? driverById.get(trip.driver_id) : undefined;
      const stops = Array.isArray(trip.stops) ? trip.stops : [];

      const baseRow = {
        'Viaje ID': trip.id,
        'Nombre viaje': trip.name,
        'Fecha viaje': trip.date,
        'Estado viaje': STATUS_LABELS[trip.status] || trip.status,
        Transportista: driver?.name || 'Sin asignar',
        'Teléfono transportista': driver?.phone || '',
        'Email transportista': driver?.email || '',
        'Tipo vehículo': driver?.vehicle_type || '',
        Patente: driver?.vehicle_plate || '',
        'Notas transportista': driver?.notes || '',
        'Costo por km': trip.cost_per_km ?? 0,
        'Distancia total km': trip.total_distance ?? 0,
        'Costo total viaje': trip.total_cost ?? 0,
        'Total paradas viaje': stops.length,
        Creado: trip.created_at ? formatArgentinaDate(trip.created_at.slice(0, 10)) : '',
        Actualizado: trip.updated_at ? formatArgentinaDate(trip.updated_at.slice(0, 10)) : '',
      };

      if (stops.length === 0) {
        rows.push({
          ...baseRow,
          'Parada nro': '',
          'Nombre parada': '',
          'Dirección parada': '',
          'Latitud parada': '',
          'Longitud parada': '',
          'Notas parada': '',
        });
        continue;
      }

      stops.forEach((stop, index) => {
        rows.push({
          ...baseRow,
          'Parada nro': index + 1,
          'Nombre parada': stop.name || '',
          'Dirección parada': stop.address || '',
          'Latitud parada': stop.lat ?? '',
          'Longitud parada': stop.lng ?? '',
          'Notas parada': stop.notes || '',
        });
      });
    }

    return rows;
  };

  const handleExportCsv = () => {
    const summaryCsv = objectsToCsv(buildSummaryRows(), [...SUMMARY_HEADERS]);
    let csv = summaryCsv;

    if (includeTripDetail) {
      const detailCsv = objectsToCsv(buildTripDetailRows(), [...DETAIL_HEADERS]);
      csv = [summaryCsv, '', '--- DETALLE POR PARADA ---', detailCsv].join('\n');
    }

    const suffix = includeTripDetail ? 'con-detalle' : 'resumen';
    downloadCsv(csv, `logistica-${suffix}-${dateStart}-${dateEnd}.csv`);
  };

  const totals = useMemo(() => {
    const totalCost = trips.reduce((s, t) => s + (t.total_cost || 0), 0);
    const totalKm = trips.reduce((s, t) => s + (t.total_distance || 0), 0);
    const totalStops = trips.reduce((s, t) => s + (Array.isArray(t.stops) ? t.stops.length : 0), 0);
    const n = trips.length;
    return {
      totalCost,
      totalKm,
      totalStops,
      trips: n,
      costPerTrip: n > 0 ? totalCost / n : 0,
      costPerKm: totalKm > 0 ? totalCost / totalKm : 0,
      kmPerTrip: n > 0 ? totalKm / n : 0,
      stopsPerTrip: n > 0 ? totalStops / n : 0,
    };
  }, [trips]);

  const driverRankingByTotalCost = useMemo(() => {
    const byDriver = new Map<string, Trip[]>();
    for (const t of trips) {
      const id = t.driver_id || 'unknown';
      if (!byDriver.has(id)) byDriver.set(id, []);
      byDriver.get(id)!.push(t);
    }
    const rows = Array.from(byDriver.entries()).map(([driverId, list]) => {
      const totalCost = list.reduce((s, t) => s + (t.total_cost || 0), 0);
      const totalKm = list.reduce((s, t) => s + (t.total_distance || 0), 0);
      const totalStops = list.reduce((s, t) => s + (Array.isArray(t.stops) ? t.stops.length : 0), 0);
      const tripsCount = list.length;
      const driverName = driverId === 'unknown' ? 'Sin conductor' : (driverNameById.get(driverId) || 'Desconocido');
      return {
        driverId,
        driverName,
        trips: tripsCount,
        totalCost,
        totalKm,
        totalStops,
        costPerTrip: tripsCount > 0 ? totalCost / tripsCount : 0,
        costPerKm: totalKm > 0 ? totalCost / totalKm : 0,
        stopsPerTrip: tripsCount > 0 ? totalStops / tripsCount : 0,
      };
    });
    return rows
      .filter(r => r.trips > 0)
      .sort((a, b) => b.totalCost - a.totalCost);
  }, [trips, driverNameById]);

  const monthBreakdown = useMemo(() => {
    const keyFor = (iso: string) => iso.slice(0, 7); // YYYY-MM
    const m = new Map<string, { cost: number; km: number; trips: number; stops: number }>();
    for (const t of trips) {
      const key = keyFor(t.date);
      if (!m.has(key)) m.set(key, { cost: 0, km: 0, trips: 0, stops: 0 });
      const v = m.get(key)!;
      v.cost += t.total_cost || 0;
      v.km += t.total_distance || 0;
      v.trips += 1;
      v.stops += Array.isArray(t.stops) ? t.stops.length : 0;
    }
    const rows = Array.from(m.entries())
      .map(([key, v]) => ({
        key,
        label: `${key.slice(5)}/${key.slice(0, 4)}`,
        ...v,
        costPerTrip: v.trips > 0 ? v.cost / v.trips : 0,
        costPerKm: v.km > 0 ? v.cost / v.km : 0,
        stopsPerTrip: v.trips > 0 ? v.stops / v.trips : 0,
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
    return rows;
  }, [trips]);

  const weekBreakdown = useMemo(() => {
    // Week key: YYYY-Www based on Monday start (rough but consistent)
    const weekKey = (iso: string) => {
      const d = new Date(iso + 'T12:00:00-03:00');
      const day = (d.getDay() + 6) % 7; // Monday=0
      const monday = new Date(d);
      monday.setDate(d.getDate() - day);
      const y = monday.getFullYear();
      const m = String(monday.getMonth() + 1).padStart(2, '0');
      const da = String(monday.getDate()).padStart(2, '0');
      return `${y}-${m}-${da}`; // start-of-week date
    };

    const m = new Map<string, { cost: number; km: number; trips: number; stops: number }>();
    for (const t of trips) {
      const key = weekKey(t.date);
      if (!m.has(key)) m.set(key, { cost: 0, km: 0, trips: 0, stops: 0 });
      const v = m.get(key)!;
      v.cost += t.total_cost || 0;
      v.km += t.total_distance || 0;
      v.trips += 1;
      v.stops += Array.isArray(t.stops) ? t.stops.length : 0;
    }

    return Array.from(m.entries())
      .map(([key, v]) => ({
        key,
        weekStart: key,
        label: `Semana ${formatArgentinaDate(key)} `,
        ...v,
        costPerTrip: v.trips > 0 ? v.cost / v.trips : 0,
        costPerKm: v.km > 0 ? v.cost / v.km : 0,
        stopsPerTrip: v.trips > 0 ? v.stops / v.trips : 0,
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [trips]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[120] p-3 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-xl max-w-7xl w-full my-8 overflow-hidden shadow-xl border border-gray-200 dark:border-gray-800">
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-4 px-6 py-4 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Analítica de Logística</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Costos y performance ({formatArgentinaDate(dateStart)} → {formatArgentinaDate(dateEnd)})
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeTripDetail}
                onChange={e => setIncludeTripDetail(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Incluir detalle de cada recorrido
            </label>
            <Button variant="ghost" size="sm" onClick={() => void loadTrips()} disabled={isLoading} className="rounded-lg">
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleExportCsv} className="rounded-lg" disabled={trips.length === 0}>
              <Download className="h-4 w-4" /> Exportar CSV
            </Button>
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Filter className="h-4 w-4" />
              Filtros
            </div>
            <div className="flex flex-wrap gap-2">
              {datePresets.map(p => {
                const isActive = dateStart === p.start && dateEnd === p.end;
                return (
                  <button
                    key={p.id}
                    onClick={() => {
                      setDateStart(p.start);
                      setDateEnd(p.end);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 border border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Desde</label>
                <input
                  type="date"
                  value={dateStart}
                  onChange={e => setDateStart(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Hasta</label>
                <input
                  type="date"
                  value={dateEnd}
                  onChange={e => setDateEnd(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Conductor</label>
                <select
                  value={driverFilter}
                  onChange={e => setDriverFilter(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm min-w-[180px]"
                >
                  <option value="all">Todos</option>
                  {drivers.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Estado</label>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm min-w-[160px]"
                >
                  <option value="all">Todos</option>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(100vh-250px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 dark:border-indigo-400" />
            </div>
          ) : trips.length === 0 ? (
            <EmptyState onClose={onClose} />
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard label="Costo total" value={formatCurrency(totals.totalCost)} icon={<Truck className="h-5 w-5" />} />
                <KpiCard label="Costo / viaje" value={formatCurrency(totals.costPerTrip)} icon={<Calendar className="h-5 w-5" />} />
                <KpiCard label="Costo / km" value={formatCurrency(totals.costPerKm)} icon={<BarChart3 className="h-5 w-5" />} />
                <KpiCard label="Viajes" value={String(totals.trips)} icon={<User className="h-5 w-5" />} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard label="Km totales" value={totals.totalKm.toFixed(0)} icon={<Truck className="h-5 w-5" />} />
                <KpiCard label="Km / viaje" value={totals.kmPerTrip.toFixed(1)} icon={<Truck className="h-5 w-5" />} />
                <KpiCard label="Paradas totales" value={String(totals.totalStops)} icon={<Truck className="h-5 w-5" />} />
                <KpiCard label="Paradas / viaje" value={totals.stopsPerTrip.toFixed(1)} icon={<Truck className="h-5 w-5" />} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="rounded-lg overflow-hidden">
                  <CardHeader title="Costo por semana" />
                  <CardContent className="pt-0">
                    <BreakdownTable
                      rows={weekBreakdown}
                      empty="Sin datos semanales."
                    />
                  </CardContent>
                </Card>
                <Card className="rounded-lg overflow-hidden">
                  <CardHeader title="Costo por mes" />
                  <CardContent className="pt-0">
                    <BreakdownTable
                      rows={monthBreakdown}
                      empty="Sin datos mensuales."
                    />
                  </CardContent>
                </Card>
              </div>

              <Card className="rounded-lg overflow-hidden">
                <CardHeader
                  title={
                    <div className="flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      Ranking de conductores
                    </div>
                  }
                />
                <CardContent className="pt-0">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="text-xs text-gray-500 dark:text-gray-400">
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-3 pr-4">#</th>
                          <th className="text-left py-3 pr-4">Conductor</th>
                          <th className="text-right py-3 px-3">Costo total</th>
                          <th className="text-right py-3 px-3">Costo / km</th>
                          <th className="text-right py-3 px-3">Costo / viaje</th>
                          <th className="text-right py-3 px-3">Viajes</th>
                          <th className="text-right py-3 pl-3">Paradas / viaje</th>
                        </tr>
                      </thead>
                      <tbody>
                        {driverRankingByTotalCost.slice(0, 20).map((r, idx) => (
                          <tr key={r.driverId} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="py-3 pr-4 text-gray-500 dark:text-gray-400">{idx + 1}</td>
                            <td className="py-3 pr-4 font-medium text-gray-900 dark:text-white">{r.driverName}</td>
                            <td className="py-3 px-3 text-right font-semibold text-gray-900 dark:text-white">{formatCurrency(r.totalCost)}</td>
                            <td className="py-3 px-3 text-right text-gray-700 dark:text-gray-200">{formatCurrency(r.costPerKm)}</td>
                            <td className="py-3 px-3 text-right text-gray-700 dark:text-gray-200">{formatCurrency(r.costPerTrip)}</td>
                            <td className="py-3 px-3 text-right text-gray-700 dark:text-gray-200">{r.trips}</td>
                            <td className="py-3 pl-3 text-right text-gray-700 dark:text-gray-200">{r.stopsPerTrip.toFixed(1)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                    Tip: ajustá el rango de fechas para comparar semanas/meses.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

function KpiCard({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <Card className="rounded-lg">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white mt-1 truncate">{value}</p>
          </div>
          <div className="p-2 rounded-lg bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300 shrink-0">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BreakdownTable({
  rows,
  empty,
}: {
  rows: Array<{
    key: string;
    label: string;
    cost: number;
    km: number;
    trips: number;
    stops: number;
    costPerTrip: number;
    costPerKm: number;
    stopsPerTrip: number;
  }>;
  empty: string;
}) {
  if (!rows.length) return <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">{empty}</div>;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="text-xs text-gray-500 dark:text-gray-400">
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th className="text-left py-3 pr-4">Período</th>
            <th className="text-right py-3 px-3">Costo</th>
            <th className="text-right py-3 px-3">Costo / km</th>
            <th className="text-right py-3 px-3">Costo / viaje</th>
            <th className="text-right py-3 px-3">Viajes</th>
            <th className="text-right py-3 pl-3">Paradas / viaje</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.key} className="border-b border-gray-100 dark:border-gray-800">
              <td className="py-3 pr-4 font-medium text-gray-900 dark:text-white">{r.label}</td>
              <td className="py-3 px-3 text-right font-semibold text-gray-900 dark:text-white">{formatCurrency(r.cost)}</td>
              <td className="py-3 px-3 text-right text-gray-700 dark:text-gray-200">{formatCurrency(r.costPerKm)}</td>
              <td className="py-3 px-3 text-right text-gray-700 dark:text-gray-200">{formatCurrency(r.costPerTrip)}</td>
              <td className="py-3 px-3 text-right text-gray-700 dark:text-gray-200">{r.trips}</td>
              <td className="py-3 pl-3 text-right text-gray-700 dark:text-gray-200">{r.stopsPerTrip.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="p-6 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
        <BarChart3 className="h-16 w-16 text-gray-400" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Sin datos para el período</h3>
      <p className="text-gray-500 dark:text-gray-400 max-w-md mb-6">
        No hay viajes que coincidan con los filtros seleccionados. Probá otro rango de fechas o conductor.
      </p>
      <Button variant="outline" onClick={onClose} className="rounded-xl">
        Cerrar
      </Button>
    </div>
  );
}

export default LogisticsAnalyticsModal;
